import React from 'react';

import Alert from '../components/Alert';
import RepaymentDetails from '../components/RepaymentDetails';

import {
  calculateRepayment,
  calculateTotalInterest,
  getRepaymentDetails,
} from '../utils/StudentLoan';

import getConfig from '../utils/getConfig';

import formatToUSD from '../utils/formatUsd';

function AdvancedCalculator() {
  const config = getConfig();
  const defaultProgram = config.programs.length > 0 ? config.programs[0] : undefined;
  const [program, setProgram] = React.useState(defaultProgram);
  const [graduateIn, setGraduateIn] = React.useState(defaultProgram?.school_period ? defaultProgram.school_period : 5);
  const [loanAmount, setLoanAmount] = React.useState(defaultProgram?.cost ? defaultProgram.cost : 12300);
  const [loanTerm, setLoanTerm] = React.useState(config.maxLoanTerm);
  const [interestRate, setInterestRate] = React.useState(config.interestRate);
  const [payInterestDuringSchoolYear, setPayInterestDuringSchoolYear] = React.useState(defaultProgram?.pay_interest_during_school_year ? defaultProgram.pay_interest_during_school_year : false);
  const [deposit, setDeposit] = React.useState(0);
  const [repayment, setRepayment] = React.useState<undefined|number>(undefined);
  const [totalInterest, setTotalInterest] = React.useState<undefined|number>(undefined);
  const [repaymentDetails, setRepaymentDetails] = React.useState<RepaymentDetail[]>([]);
  const [financedAmount, setFinancedAmount] = React.useState<number|undefined>(undefined);
  const [errorMessage, setErrorMessage] = React.useState("");
  let stats = [
    {name: "Repayment", value: (repayment === undefined) ? '-' : (<span>{formatToUSD(repayment)}<span className="text-xs text-gray-500">/month</span></span>) },
    {name: "Total Interest", value: (totalInterest === undefined) ? '-' : formatToUSD(totalInterest)},
    {name: "Financed Amount", value: (financedAmount === undefined) ? '-' : formatToUSD(financedAmount)}
  ];

  const calculate = () => {
    if (loanAmount < 0) {
      setErrorMessage('Invalid loan amount!');
      return;
    }
    if (deposit >= loanAmount) {
      setErrorMessage('Deposit must be lower than loan amount!');
      return;
    }
    if (deposit < 0) {
      setErrorMessage('Invalid deposit amount!');
      return;
    }
    if (loanTerm > config.maxLoanTerm) {
      setErrorMessage(`Loan term cannot be larger than ${config.maxLoanTerm} months!`);
      return;
    }
    if (loanTerm < 0) {
      setErrorMessage('Invalid loan term value!');
      return;
    }
    if (interestRate > 100) {
      setErrorMessage(`Interest rate cannot be larger than 100%`);
      return;
    }
    if (interestRate < 0) {
      setErrorMessage('Invalid interest rate value!');
      return;
    }
    if (isNaN(graduateIn)) {
      setErrorMessage('Invalid school period value!');
      return;
    }
    if (isNaN(loanAmount)) {
      setErrorMessage('Invalid loan amount value!');
      return;
    }
    if (isNaN(loanTerm)) {
      setErrorMessage('Invalid loan term value!');
      return;
    }
    if (isNaN(interestRate)) {
      setErrorMessage('Invalid interest rate value!');
      return;
    }
    if (isNaN(deposit)) {
      setErrorMessage('Invalid deposit value!');
      return;
    }
    setErrorMessage('');
    let repayment = calculateRepayment(graduateIn, loanAmount - deposit, loanTerm, interestRate, payInterestDuringSchoolYear);
    let totalInterest = calculateTotalInterest(graduateIn, loanAmount - deposit, loanTerm, interestRate, payInterestDuringSchoolYear, repayment);
    let repaymentDetails = getRepaymentDetails(graduateIn, loanAmount - deposit, loanTerm, interestRate, payInterestDuringSchoolYear, repayment)
    // console.log(repaymentDetails);
    setRepaymentDetails(repaymentDetails);
    setRepayment(repayment);
    setTotalInterest(totalInterest);
    setFinancedAmount(loanAmount - deposit + totalInterest);
  }

  React.useEffect(() => {
    calculate();
  }, []);

  React.useEffect(() => {
    calculate();
  }, [program, graduateIn, loanAmount, loanTerm, interestRate, payInterestDuringSchoolYear, deposit]);

  return (
    <div className="md:flex md:items-start p-4 h-screen">
      <div className="mr-4 md:min-w-[300px]">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            calculate();
          }}
        >
          <div>
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
            >Program</label>
            <select
              id="program"
              defaultValue={program?.name}
              onChange={(e) => {
                config.programs
                  .filter(p => p.name === e.target.value)
                  .forEach(p => {
                    setProgram(p);
                    setGraduateIn(p.school_period);
                    setLoanAmount(p.cost);
                    setPayInterestDuringSchoolYear(p.pay_interest_during_school_year);
                  });
              }}
              className="custom-select bg-white mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              {config.programs.map((item, i) => (
                <option key={`program-${i}`} value={item.name}>{item.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
            >Graduate in (month)</label>
            <input
              type="number"
              value={graduateIn}
              min={0}
              onChange={(e) => setGraduateIn(parseInt(e.target.value))}
              className="block w-full rounded-md border-0 p-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
            >Loan amount</label>
            <input
              type="number"
              value={loanAmount}
              min={deposit}
              onChange={(e) => setLoanAmount(parseFloat(e.target.value))}
              className="block w-full rounded-md border-0 p-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
            >Loan term (month)</label>
            <input
              type="number"
              value={loanTerm}
              min={0}
              max={config.maxLoanTerm}
              onChange={(e) => setLoanTerm(parseInt(e.target.value))}
              className="block w-full rounded-md border-0 p-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            <input
              type="range"
              value={loanTerm}
              min={0}
              max={config.maxLoanTerm}
              onChange={(e) => setLoanTerm(parseInt(e.target.value))}
              className="w-full h-2 mt-4 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
            >Interest rate</label>
            <input
              type="number"
              value={interestRate}
              min={0}
              step={0.1}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="block w-full rounded-md border-0 p-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
          <div className="flex items-center">
            <input
              id="pay_interest_during_school_year"
              type="checkbox"
              checked={payInterestDuringSchoolYear}
              onChange={(e) => {
                if (e.target.checked) setPayInterestDuringSchoolYear(true);
                else setPayInterestDuringSchoolYear(false);
              }}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            />
            <label
              htmlFor="pay_interest_during_school_year"
              className="ml-3 block text-sm font-medium leading-6 text-gray-900"
            >Pay interest during school years</label>
          </div>
          <div>
            <label
              className="block text-sm font-medium leading-6 text-gray-900"
            >Deposit ($)</label>
            <input
              type="number"
              value={deposit}
              min={0}
              max={loanAmount}
              onChange={(e) => setDeposit(parseFloat(e.target.value))}
              className="block w-full rounded-md border-0 p-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />

            <input
              type="range"
              value={deposit}
              min={0}
              max={loanAmount - 100}
              step={100}
              onChange={(e) => setDeposit(parseInt(e.target.value))}
              className="w-full h-2 mt-4 mb-8 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
          </div>
          {/* <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >Calculate</button>
          </div> */}

          {!!errorMessage && <Alert type="error" message={errorMessage} />}
        </form>

        <div className="mt-4 pointer-events-auto overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <dl className="max-w-[400px] mx-auto grid grid-cols-1 gap-px bg-gray-900/5 sm:grid-cols-1 lg:grid-cols-3">
            {stats.map((stat) => (
              <div
                key={stat.name}
                className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-2 bg-white px-2 py-4 sm:px-2 xl:px-4"
              >
                <dt className="text-xs font-medium leading-6 text-gray-500">{stat.name}</dt>
                <dd className="w-full flex-none text-lg font-medium leading-4 tracking-tight text-gray-900">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>

        </div>
      </div>

      <RepaymentDetails repaymentDetails={repaymentDetails} />
    </div>
  );
}

export default AdvancedCalculator;